import { useCallback, useEffect, useState } from 'react';

import {
  bulkModerateComments,
  deleteComment,
  dismissCommentReports,
  listComments,
  moderateComment,
  updateCommentText,
  type BulkCommentAction,
  type CommentCounts,
  type CommentListItem,
  type CommentReportReason,
  type CommentStatus,
  type CommentStatusFilter,
  type ListCommentsParams,
} from '../../lib/panelApi';

type Props = {
  accessToken: string;
};

type Filters = {
  status: CommentStatusFilter;
  q: string;
  sort: 'created_at' | 'moderated_at';
  order: 'asc' | 'desc';
  limit: number;
  offset: number;
};

const DEFAULT_FILTERS: Filters = {
  status: 'pending',
  q: '',
  sort: 'created_at',
  // Oldest first: the queue is a backlog, and the reader who has been
  // waiting longest should be unblocked first.
  order: 'asc',
  limit: 25,
  offset: 0,
};

const EMPTY_COUNTS: CommentCounts = {
  pending: 0,
  approved: 0,
  rejected: 0,
  reported: 0,
  total: 0,
};

const TABS: { key: Filters['status']; label: string; countKey: keyof CommentCounts }[] = [
  { key: 'pending', label: 'Pending', countKey: 'pending' },
  // Second, not last: a flag against something already public is more
  // urgent than the rest of the backlog.
  { key: 'reported', label: 'Reported', countKey: 'reported' },
  { key: 'approved', label: 'Published', countKey: 'approved' },
  { key: 'rejected', label: 'Rejected', countKey: 'rejected' },
  { key: 'all', label: 'All', countKey: 'total' },
];

const REASON_LABELS: Record<CommentReportReason, string> = {
  spam: 'Spam',
  harassment: 'Harassment',
  hate: 'Hate speech',
  sexual: 'Sexual',
  violence: 'Violence',
  misinformation: 'Misinformation',
  other: 'Other',
};

/**
 * Comment moderation queue.
 *
 * Reader comments arrive from the public site as `pending` and stay
 * invisible until they are approved here. The screen is built around
 * clearing that backlog: oldest-first by default, keyboard-free bulk
 * actions, and every decision reversible (a published comment can go
 * back to the queue, a rejected one can be published).
 */
export function CommentsPage({ accessToken }: Props) {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [items, setItems] = useState<CommentListItem[]>([]);
  const [counts, setCounts] = useState<CommentCounts>(EMPTY_COUNTS);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const refresh = useCallback(
    async (overrides?: Partial<Filters>) => {
      const next = { ...filters, ...(overrides ?? {}) };
      setFilters(next);
      setLoading(true);
      setError(null);
      try {
        const params: ListCommentsParams = {
          status: next.status,
          q: next.q,
          sort: next.sort,
          order: next.order,
          limit: next.limit,
          offset: next.offset,
        };
        const res = await listComments(accessToken, params);
        setItems(res.items);
        setCounts(res.counts ?? EMPTY_COUNTS);
        setTotal(res.total);
        setSelected(new Set());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load comments');
      } finally {
        setLoading(false);
      }
    },
    [accessToken, filters],
  );

  useEffect(() => {
    void refresh({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPageSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const toggleSelectAll = () => {
    setSelected(allOnPageSelected ? new Set() : new Set(items.map((c) => c.id)));
  };

  const runBulk = async (action: BulkCommentAction) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    setError(null);
    try {
      const res = await bulkModerateComments(accessToken, Array.from(selected), action);
      setNotice(`${res.affected} comment${res.affected === 1 ? '' : 's'} ${pastTense(action)}.`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const onModerate = async (id: string, status: CommentStatus, note?: string) => {
    await moderateComment(accessToken, id, status, note ?? '');
    setNotice(`Comment ${status === 'approved' ? 'published' : status === 'rejected' ? 'rejected' : 'sent back to the queue'}.`);
    await refresh();
  };

  const onDelete = async (id: string) => {
    await deleteComment(accessToken, id);
    setNotice('Comment deleted.');
    await refresh();
  };

  const onEdit = async (id: string, text: string) => {
    await updateCommentText(accessToken, id, text);
    setNotice('Comment text updated.');
    await refresh();
  };

  const onDismissReports = async (id: string) => {
    const res = await dismissCommentReports(accessToken, id);
    setNotice(
      `${res.dismissed} report${res.dismissed === 1 ? '' : 's'} dismissed. The comment is unchanged.`,
    );
    await refresh();
  };

  const onPage = (delta: number) => {
    void refresh({ offset: Math.max(0, filters.offset + delta * filters.limit) });
  };

  return (
    <div className="card">
      <div className="header" style={{ marginBottom: 8 }}>
        <div>
          <div className="sectionTitle" style={{ margin: 0 }}>
            Comments
          </div>
          <div className="subtitle">
            Reader comments stay hidden until you publish them. Authors see their own pending
            comment labelled “awaiting review”.
          </div>
        </div>
        <button className="btn" onClick={() => refresh()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Status tabs — counts respect the current search. */}
      <div className="row" style={{ gap: 8, marginBottom: 10 }}>
        {TABS.map((t) => {
          const active = filters.status === t.key;
          return (
            <button
              key={t.key}
              type="button"
              className={`btn ${active ? 'btn-primary' : ''}`}
              onClick={() => void refresh({ status: t.key, offset: 0 })}
              disabled={loading}
              style={{ padding: '9px 14px' }}
            >
              {t.label}
              <span
                style={{
                  marginLeft: 8,
                  fontWeight: 900,
                  opacity: active ? 0.85 : 0.5,
                }}
              >
                {counts[t.countKey]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="row" style={{ marginBottom: 10, alignItems: 'end', gap: 8, flexWrap: 'wrap' }}>
        <div className="field" style={{ marginTop: 0, flex: '1 1 260px', minWidth: 220 }}>
          <div className="label">Search</div>
          <input
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void refresh({ offset: 0 });
            }}
            placeholder="Search comment text, reader or book title…"
          />
        </div>

        <div className="field" style={{ marginTop: 0 }}>
          <div className="label">Sort</div>
          <select
            value={filters.sort}
            onChange={(e) => void refresh({ sort: e.target.value as Filters['sort'], offset: 0 })}
          >
            <option value="created_at">Submitted</option>
            <option value="moderated_at">Reviewed</option>
          </select>
        </div>

        <div className="field" style={{ marginTop: 0 }}>
          <div className="label">Order</div>
          <select
            value={filters.order}
            onChange={(e) => void refresh({ order: e.target.value as Filters['order'], offset: 0 })}
          >
            <option value="asc">Oldest first</option>
            <option value="desc">Newest first</option>
          </select>
        </div>

        <button className="btn btn-secondary" onClick={() => refresh({ offset: 0 })} disabled={loading}>
          Search
        </button>
        <button className="btn" onClick={() => void refresh({ ...DEFAULT_FILTERS })} disabled={loading}>
          Reset
        </button>
      </div>

      {error ? (
        <div style={{ marginBottom: 10 }}>
          <div className="errorBox">{error}</div>
        </div>
      ) : null}

      {notice ? (
        <div
          className="pill pill-ok"
          style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}
        >
          <span>{notice}</span>
          <button
            className="btn"
            style={{ padding: '2px 10px', fontSize: 11 }}
            onClick={() => setNotice(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {/* Bulk action bar. */}
      {items.length > 0 ? (
        <div
          className="row"
          style={{ alignItems: 'center', gap: 10, marginBottom: 10, justifyContent: 'space-between' }}
        >
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} />
            <span className="muted">
              {selected.size > 0 ? `${selected.size} selected` : 'Select all on this page'}
            </span>
          </label>

          {selected.size > 0 ? (
            <div className="row" style={{ gap: 8 }}>
              <button
                className="btn btn-primary"
                disabled={bulkBusy}
                onClick={() => void runBulk('approve')}
              >
                Publish
              </button>
              <button className="btn" disabled={bulkBusy} onClick={() => void runBulk('reject')}>
                Reject
              </button>
              <button className="btn" disabled={bulkBusy} onClick={() => void runBulk('pending')}>
                Back to queue
              </button>
              <button
                className="btn btn-danger"
                disabled={bulkBusy}
                onClick={() => void runBulk('delete')}
              >
                Delete
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="muted">Loading comments…</div>
      ) : items.length === 0 ? (
        <div className="muted">
          {filters.status === 'pending'
            ? 'Nothing waiting for review. The queue is clear.'
            : filters.status === 'reported'
              ? 'No open reader reports. Nothing has been flagged.'
              : 'No comments match these filters.'}
        </div>
      ) : (
        <>
          <div className="list">
            {items.map((c) => (
              <CommentCard
                key={c.id}
                comment={c}
                selected={selected.has(c.id)}
                onToggleSelect={() => toggleSelected(c.id)}
                onModerate={onModerate}
                onDelete={onDelete}
                onEdit={onEdit}
                onDismissReports={onDismissReports}
              />
            ))}
          </div>

          <div
            className="row"
            style={{ marginTop: 12, alignItems: 'center', gap: 12, justifyContent: 'space-between' }}
          >
            <div className="muted" style={{ fontSize: 12 }}>
              Showing {filters.offset + 1}–{Math.min(filters.offset + items.length, total)} of {total}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn" disabled={filters.offset === 0 || loading} onClick={() => onPage(-1)}>
                ← Prev
              </button>
              <button
                className="btn"
                disabled={filters.offset + items.length >= total || loading}
                onClick={() => onPage(1)}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ───── Card ───── */

type CardMode = 'idle' | 'reject' | 'edit' | 'delete';

function CommentCard({
  comment,
  selected,
  onToggleSelect,
  onModerate,
  onDelete,
  onEdit,
  onDismissReports,
}: {
  comment: CommentListItem;
  selected: boolean;
  onToggleSelect: () => void;
  onModerate: (id: string, status: CommentStatus, note?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onEdit: (id: string, text: string) => Promise<void>;
  onDismissReports: (id: string) => Promise<void>;
}) {
  const [mode, setMode] = useState<CardMode>('idle');
  const [note, setNote] = useState('');
  const [draft, setDraft] = useState(comment.text);
  const [busy, setBusy] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const flagged = comment.reports_open > 0;

  // Every action funnels through here so one failure can't leave the
  // card stuck in a busy state.
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setCardError(null);
    try {
      await fn();
    } catch (err) {
      setCardError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="item"
      style={{
        // Selection wins the border, but an unselected flagged comment
        // still has to be findable at a glance in a long list.
        borderColor: selected
          ? 'rgba(204,255,0,0.75)'
          : flagged
            ? 'rgba(255,138,76,0.7)'
            : undefined,
        background: selected
          ? 'rgba(204,255,0,0.06)'
          : flagged
            ? 'rgba(255,138,76,0.05)'
            : undefined,
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          style={{ marginTop: 6 }}
          aria-label="Select comment"
        />

        <Avatar src={comment.user_picture_url} name={comment.user_name || comment.user_email} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 850 }}>{comment.user_name || 'Reader'}</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {comment.user_email}
            </span>
            <StatusPill status={comment.status} />
            {flagged ? <ReportPill count={comment.reports_open} /> : null}
          </div>

          <div className="muted" style={{ marginTop: 3, fontSize: 12 }}>
            on <strong style={{ fontWeight: 750 }}>{comment.book_title || comment.book_uuid}</strong>
            {' • '}
            {formatDateTime(comment.created_at)}
            {comment.likes_count > 0 ? ` • ${comment.likes_count} likes` : ''}
          </div>

          {flagged ? (
            <div
              style={{
                marginTop: 8,
                padding: '8px 10px',
                borderRadius: 10,
                border: '1px solid rgba(255,138,76,0.35)',
                background: 'rgba(255,138,76,0.08)',
                fontSize: 12,
                lineHeight: 1.55,
              }}
            >
              <strong style={{ fontWeight: 850 }}>
                Reported by {comment.reports_open} reader{comment.reports_open === 1 ? '' : 's'}
              </strong>
              {comment.report_reasons.length > 0 ? (
                <>
                  {' — '}
                  {comment.report_reasons
                    .map((reason) => REASON_LABELS[reason] ?? reason)
                    .join(', ')}
                </>
              ) : null}
              <div className="muted" style={{ marginTop: 2, fontSize: 11 }}>
                Publishing or rejecting also closes these reports. Use “Dismiss reports” to keep
                the comment as it is.
              </div>
            </div>
          ) : null}

          {mode === 'edit' ? (
            <div className="field" style={{ marginTop: 8 }}>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                style={{ width: '100%' }}
              />
              <div className="muted" style={{ fontSize: 11 }}>
                Redact only. The original text is kept in the activity log.
              </div>
            </div>
          ) : (
            <p
              style={{
                margin: '8px 0 0',
                fontSize: 14,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {comment.text}
            </p>
          )}

          {comment.moderated_at ? (
            <div className="muted" style={{ marginTop: 6, fontSize: 11 }}>
              Reviewed by {comment.moderated_by || 'an admin'} • {formatDateTime(comment.moderated_at)}
              {comment.moderation_note ? ` • “${comment.moderation_note}”` : ''}
            </div>
          ) : null}

          {cardError ? (
            <div className="errorBox" style={{ marginTop: 8 }}>
              {cardError}
            </div>
          ) : null}

          {/* Reject asks for an optional internal note before committing. */}
          {mode === 'reject' ? (
            <div className="row" style={{ marginTop: 10, gap: 8, alignItems: 'end' }}>
              <div className="field" style={{ marginTop: 0, flex: '1 1 240px' }}>
                <div className="label">Reason (internal, optional)</div>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Spam, abusive language, off-topic…"
                  autoFocus
                />
              </div>
              <button
                className="btn btn-danger"
                disabled={busy}
                onClick={() => void run(() => onModerate(comment.id, 'rejected', note))}
              >
                Confirm reject
              </button>
              <button className="btn" disabled={busy} onClick={() => setMode('idle')}>
                Cancel
              </button>
            </div>
          ) : mode === 'delete' ? (
            <div className="row" style={{ marginTop: 10, gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13 }}>
                Delete permanently? Rejecting hides it but keeps the record.
              </span>
              <button
                className="btn btn-danger"
                disabled={busy}
                onClick={() => void run(() => onDelete(comment.id))}
              >
                Yes, delete
              </button>
              <button className="btn" disabled={busy} onClick={() => setMode('idle')}>
                Cancel
              </button>
            </div>
          ) : mode === 'edit' ? (
            <div className="row" style={{ marginTop: 4, gap: 8 }}>
              <button
                className="btn btn-primary"
                disabled={busy || draft.trim().length < 2 || draft === comment.text}
                onClick={() => void run(() => onEdit(comment.id, draft.trim()))}
              >
                Save text
              </button>
              <button
                className="btn"
                disabled={busy}
                onClick={() => {
                  setDraft(comment.text);
                  setMode('idle');
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="row" style={{ marginTop: 10, gap: 8 }}>
              {comment.status !== 'approved' ? (
                <button
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => void run(() => onModerate(comment.id, 'approved'))}
                >
                  Publish
                </button>
              ) : null}
              {comment.status !== 'rejected' ? (
                <button className="btn" disabled={busy} onClick={() => setMode('reject')}>
                  Reject
                </button>
              ) : null}
              {comment.status !== 'pending' ? (
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => void run(() => onModerate(comment.id, 'pending'))}
                >
                  Back to queue
                </button>
              ) : null}
              {flagged ? (
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => void run(() => onDismissReports(comment.id))}
                >
                  Dismiss reports
                </button>
              ) : null}
              <button className="btn" disabled={busy} onClick={() => setMode('edit')}>
                Edit text
              </button>
              <button className="btn btn-danger" disabled={busy} onClick={() => setMode('delete')}>
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: CommentStatus }) {
  if (status === 'approved') return <span className="pill pill-ok">Published</span>;
  if (status === 'rejected') return <span className="pill pill-bad">Rejected</span>;
  return <span className="pill">Pending review</span>;
}

function ReportPill({ count }: { count: number }) {
  return (
    <span
      className="pill"
      style={{
        borderColor: 'rgba(255,138,76,0.55)',
        background: 'rgba(255,138,76,0.12)',
        color: '#ff8a4c',
        fontWeight: 850,
      }}
    >
      {count} report{count === 1 ? '' : 's'}
    </span>
  );
}

function Avatar({ src, name }: { src: string; name: string }) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={36}
        height={36}
        style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'var(--accent, #0a192f)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 900,
        flexShrink: 0,
      }}
    >
      {name.trim().slice(0, 1).toUpperCase() || '?'}
    </div>
  );
}

function pastTense(action: BulkCommentAction): string {
  switch (action) {
    case 'approve':
      return 'published';
    case 'reject':
      return 'rejected';
    case 'pending':
      return 'sent back to the queue';
    case 'delete':
      return 'deleted';
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
